import { useAvailableTenants } from '@/api/hooks/useAvailableTenants'
import type { Tenant } from '@/stores/tenantStore'
import { message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TenantSelection } from '../types'
import { getSavedTenant, saveTenantSelection } from '../utils'

interface UseTenantSelectionProps {
  entityType: string
}

interface UseTenantSelectionReturn extends TenantSelection {
  tenants: Tenant[]
  isLoadingTenants: boolean
  handleLeftTenantChange: (tenant: string) => void
  handleRightTenantChange: (tenant: string) => void
  handleSwapTenants: () => void
}

/**
 * Hook for managing tenant selection in three-way mapping view
 */
export const useTenantSelection = ({
  entityType,
}: UseTenantSelectionProps): UseTenantSelectionReturn => {
  const { availableTenants, isLoadingTenants } = useAvailableTenants()

  // Filter out Gravitate tenant
  const tenants = useMemo(() => {
    return availableTenants.filter((t) => t.name !== 'Gravitate')
  }, [availableTenants])

  // State for selected tenants - initialize with saved values or 'none'
  const [leftTenant, setLeftTenant] = useState<string>(() =>
    getSavedTenant(entityType, 'left'),
  )

  const [rightTenant, setRightTenant] = useState<string>(() =>
    getSavedTenant(entityType, 'right'),
  )

  // Set default tenants when tenants load if current selection is 'none'
  useEffect(() => {
    if (tenants.length > 0) {
      if (leftTenant === 'none') {
        const saved = getSavedTenant(entityType, 'left')
        // If we have a saved value and it exists in tenants, use it; otherwise use first tenant
        if (saved !== 'none' && tenants.some((t) => t.name === saved)) {
          setLeftTenant(saved)
        } else if (tenants[0]) {
          setLeftTenant(tenants[0].name)
        }
      }

      if (rightTenant === 'none') {
        const saved = getSavedTenant(entityType, 'right')
        // If we have a saved value and it exists in tenants, use it; otherwise use second or first tenant
        if (saved !== 'none' && tenants.some((t) => t.name === saved)) {
          setRightTenant(saved)
        } else if (tenants[1]) {
          setRightTenant(tenants[1].name)
        } else if (tenants[0]) {
          setRightTenant(tenants[0].name)
        }
      }
    }
  }, [tenants, entityType, leftTenant, rightTenant])

  // Persist tenant selections
  useEffect(() => {
    if (leftTenant !== 'none') {
      saveTenantSelection(entityType, 'left', leftTenant)
    }
  }, [leftTenant, entityType])

  useEffect(() => {
    if (rightTenant !== 'none') {
      saveTenantSelection(entityType, 'right', rightTenant)
    }
  }, [rightTenant, entityType])

  // Handle tenant selection with validation
  const handleLeftTenantChange = useCallback(
    (value: string) => {
      if (value === rightTenant) {
        message.warning('Cannot select the same tenant for both sides')
        return
      }
      setLeftTenant(value)
    },
    [rightTenant],
  )

  const handleRightTenantChange = useCallback(
    (value: string) => {
      if (value === leftTenant) {
        message.warning('Cannot select the same tenant for both sides')
        return
      }
      setRightTenant(value)
    },
    [leftTenant],
  )

  // Swap tenants
  const handleSwapTenants = useCallback(() => {
    const temp = leftTenant
    setLeftTenant(rightTenant)
    setRightTenant(temp)
  }, [leftTenant, rightTenant])

  return {
    leftTenant,
    rightTenant,
    tenants,
    isLoadingTenants,
    handleLeftTenantChange,
    handleRightTenantChange,
    handleSwapTenants,
  }
}
